import React, { useCallback, useMemo } from 'react';
import Filters from 'src/@vymo/ui/molecules/filters';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { getEntityListParams } from 'src/models/details/selectors';
import { useFilters } from 'src/models/filters/hooks';
import { setFilters } from 'src/models/filters/slice';
import {
  getAllUsers,
  getHierarchy,
  getRevyreConfig,
  getUsers,
} from 'src/models/lmsConfig/selectors';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getEntityListMetaData, getListFilterFields } from '../selectors';
import { fetchEntityList } from '../thunk';
import { DomainType, WorkflowType } from '../types';

function RevyreFilters({
  workflowType,
  domainType,
}: {
  workflowType: WorkflowType;
  domainType: DomainType;
}) {
  const dispatch = useAppDispatch();
  const allUsers = useAppSelector(getAllUsers);
  const hierarchy = useAppSelector(getHierarchy);
  const workflowConfig = useAppSelector(getRevyreConfig);
  const users = useAppSelector(getUsers);

  const filterFields = useAppSelector((state) =>
    getListFilterFields(state, domainType, workflowType),
  );
  const entityListParams = useAppSelector(getEntityListParams);
  const { pageSize } = useAppSelector(getEntityListMetaData);
  const filterContext = useMemo(
    () => `revyre_${domainType}_${workflowType || 'default'}`,
    [domainType, workflowType],
  );
  const metaData = useMemo(() => {
    const hierarchyData = hierarchy || {};
    const workflowConfigData = workflowConfig || {};
    const usersData = users || {};
    return {
      allUsers,
      userHierarchy: hierarchyData.user || {},
      hierarchy: hierarchyData.revyreHierarchy || {},
      workflowConfig: workflowConfigData,
      users: usersData,
    };
  }, [allUsers, hierarchy, workflowConfig, users]);

  const onApply = useMemo(
    () => (appliedFilters: Record<string, any>) => {
      dispatch(
        fetchEntityList({
          domainType,
          payload: {
            ...entityListParams,
            filters: appliedFilters,
            page: { pageSize, pageNumber: 1 },
          },
        }),
      );
    },
    [dispatch, domainType, entityListParams, pageSize],
  );

  const onReset = useMemo(
    () => () => {
      dispatch(
        fetchEntityList({
          domainType,
          payload: {
            ...entityListParams,
            filters: {},
            page: { pageSize, pageNumber: 1 },
          },
        }),
      );
    },
    [dispatch, domainType, entityListParams, pageSize],
  );
  const { filters, filterCount, handleApply, handleReset } = useFilters({
    context: filterContext,
    filterFields: filterFields || [],
    metaData,
    formVersion: FormVersion.webRevyre,
    onApply,
    onReset,
  });

  const handleFiltersChange = useCallback(
    (persistedFilters: Record<string, any>) => {
      dispatch(
        setFilters({ context: filterContext, filters: persistedFilters }),
      );
    },
    [dispatch, filterContext],
  );

  useFilterPersistence(filterContext, filters || {}, handleFiltersChange);

  return (
    <Filters
      filterFields={filterFields || []}
      metaData={metaData}
      formVersion={FormVersion.webRevyre}
      filters={filters}
      filterCount={filterCount}
      onApply={handleApply}
      onReset={handleReset}
    />
  );
}

export default RevyreFilters;
